import React, { useState, useRef, useEffect } from "react";
import { format, startOfMonth, endOfMonth, addMonths, subMonths } from "date-fns";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";

const MonthPicker = ({ onChange, defaultValue }) => {
  const [selectedDate, setSelectedDate] = useState(
    defaultValue ? new Date(defaultValue) : new Date()
  );
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('left');
  const containerRef = useRef(null);

  const handleYearChange = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setFullYear(selectedDate.getFullYear() + (direction === 'next' ? 1 : -1));
    setSelectedDate(newDate);
  };

  const handleMonthSelect = (month, year) => {
    const newDate = new Date(year, month, 1);
    setSelectedDate(newDate);
    
    // Calculate the start and end of the selected month
    const start = startOfMonth(newDate);
    const end = endOfMonth(newDate);
    
    
    // Call the onChange callback with the date range
    onChange([start, end]);
    setIsOpen(false);
  };

  const currentYear = selectedDate.getFullYear();
  const currentMonth = selectedDate.getMonth();

  // Generate months for the current year
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  // Calculate dropdown position
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const timer = setTimeout(() => {
        const rect = containerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const dropdownWidth = 256; // w-64 = 16rem = 256px
        
        // Check if there's enough space on the right
        const spaceOnRight = viewportWidth - rect.right;
        
        // If not enough space on the right, position dropdown to the left (right-0)
        // Otherwise, position it to align with the button (left-0)
        if (spaceOnRight < dropdownWidth) {
          setDropdownPosition('right'); // This uses right-0 class
        } else {
          setDropdownPosition('left'); // This uses left-0 class  
        }
      }, 10);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg text-white glass-button dark:glass-button-dark backdrop-blur-lg border border-white/10 hover:border-white/20 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all duration-300"
      >
        <Calendar className="w-4 h-4" />
        <span>{format(selectedDate, 'MMM yyyy')}</span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className={
          `absolute top-full mt-2 w-64 portal-gradient portal-dots-pattern backdrop-blur-xl backdrop-saturate-150 border border-white/10 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.12)] z-50 hover:shadow-[0_12px_40px_rgba(0,0,0,0.15)] transition-all duration-300 overflow-hidden ${dropdownPosition === 'right' ? 'right-0' : 'left-0'}`
        }>
          {/* Background Overlays */}
          <div className="absolute inset-0 portal-gradient-overlay"></div>
          <div className="absolute inset-0 portal-mesh-pattern opacity-30"></div>
          
          {/* Content Container */}
          <div className="relative z-10">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <button
                onClick={() => handleYearChange('prev')}
                className="p-2 hover:bg-white/10 rounded-lg text-white/70 hover:text-white/90 transition-all duration-200 hover:shadow-[0_4px_16px_rgba(255,255,255,0.05)]"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="font-semibold text-white/90 poppins-semibold tracking-wide">
                {currentYear}
              </span>
              <button
                onClick={() => handleYearChange('next')}
                className="p-2 hover:bg-white/10 rounded-lg text-white/70 hover:text-white/90 transition-all duration-200 hover:shadow-[0_4px_16px_rgba(255,255,255,0.05)]"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Months Grid */}
            <div className="p-4">
              <div className="grid grid-cols-3 gap-3">
                {months.map((month, index) => (
                  <button
                    key={index}
                    onClick={() => handleMonthSelect(index, currentYear)}
                    className={`p-3 text-sm rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/20 poppins-medium ${
                      index === currentMonth
                        ? 'bg-white/20 text-white/95 font-semibold shadow-[0_4px_16px_rgba(255,255,255,0.1)] border border-white/20'
                        : 'text-white/70 hover:bg-white/10 hover:text-white/90 hover:shadow-[0_4px_16px_rgba(255,255,255,0.05)]'
                    }`}
                  >
                    {month}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="p-4 border-t border-white/10">
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => {
                    const now = new Date();
                    setSelectedDate(now);
                    onChange([startOfMonth(now), endOfMonth(now)]);
                    setIsOpen(false);
                  }}
                  className="text-xs text-white/70 hover:text-white/90 transition-all duration-200 py-2.5 px-3 rounded-lg hover:bg-white/10 poppins-light tracking-wide hover:shadow-[0_4px_16px_rgba(255,255,255,0.05)]"
                >
                  This Month
                </button>
                <button
                  onClick={() => {
                    const lastMonth = subMonths(new Date(), 1);
                    setSelectedDate(lastMonth);
                    onChange([startOfMonth(lastMonth), endOfMonth(lastMonth)]);
                    setIsOpen(false);
                  }}
                  className="text-xs text-white/70 hover:text-white/90 transition-all duration-200 py-2.5 px-3 rounded-lg hover:bg-white/10 poppins-light tracking-wide hover:shadow-[0_4px_16px_rgba(255,255,255,0.05)]"
                >
                  Last Month
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default MonthPicker;